import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/stores/auth';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  ChevronDown, 
  ChevronUp, 
  Play, 
  CheckCircle, 
  Lock, 
  FileText,
  Download,
  Clock,
  Award
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { studentApi } from '@/lib/api';
import { toast } from 'sonner';
import { ExamRequestForm } from '@/components/ExamRequestForm';

export default function StudentCoursePage() {
  const { courseId } = useParams();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const handleMarkIncomplete = async (lessonId: string) => {
    try {
      const response = await fetch(`/api/student/lessons/${lessonId}/complete`, { method: 'POST', credentials: 'include' });
      if (!response.ok) throw new Error('Failed');
      queryClient.invalidateQueries();
      toast.success('Lesson marked as incomplete');
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());

  const { data, isLoading, error } = useQuery({
    queryKey: ['student-course', courseId],
    queryFn: () => studentApi.getCourse(courseId!),
    refetchOnWindowFocus: true,
    enabled: !!courseId,
  });

  const toggleModule = (moduleId: string) => {
    const newExpanded = new Set(expandedModules);
    if (newExpanded.has(moduleId)) {
      newExpanded.delete(moduleId);
    } else {
      newExpanded.add(moduleId);
    }
    setExpandedModules(newExpanded);
  };

  if (isLoading) {
    return (
      <div className="container py-12">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="container py-12">
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-red-600 mb-4">
              {error instanceof Error ? error.message : 'Failed to load course'}
            </p>
            <Button asChild>
              <Link to="/dashboard">Back to Dashboard</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const course = data.course;
  const enrollment = data.enrollment;
  const modules = data.modules || [];

  // Calculate completed lessons
  const totalLessons = modules.reduce((acc, m) => acc + m.lessons.length, 0);
  const completedLessons = modules.reduce(
    (acc, m) => acc + m.lessons.filter(l => l.progress?.isCompleted).length,
    0
  );

  // Find first incomplete lesson for "Resume" button
  // Handle certificate download
  const handleDownloadCertificate = async () => {
    try {
      const response = await fetch(`/api/student/courses/${courseId}/certificate/download`, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to download certificate");
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `certificate-${courseId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Certificate download error:", error);
      alert("Failed to download certificate. Please try again.");
    }
  };

  // Find the last watched lesson (with most recent progress) or first incomplete
  const lastWatchedLesson = modules
    .flatMap(m => m.lessons.map(l => ({ ...l, moduleId: m.id })))
    .filter(l => l.progress?.lastPosition && l.progress.lastPosition > 0)
    .sort((a, b) => {
      const aTime = a.progress?.updatedAt ? new Date(a.progress.updatedAt).getTime() : 0;
      const bTime = b.progress?.updatedAt ? new Date(b.progress.updatedAt).getTime() : 0;
      return bTime - aTime;
    })[0] || modules
    .flatMap(m => m.lessons.map(l => ({ ...l, moduleId: m.id })))
    .find(l => !l.progress?.isCompleted);
  const firstIncompleteLesson = lastWatchedLesson;

  return (
    <div className="py-8">
      <div className="container">
        {/* Course Header */}
        <div className="mb-8">
          <Link 
            to="/dashboard" 
            className="text-sm text-muted-foreground hover:text-primary mb-4 inline-block"
          >
            ← {t('common.backToDashboard') || 'Back to Dashboard'}
          </Link>
          
          <h1 className="text-3xl font-bold mb-4">
            {isRTL ? course.titleAr : course.titleEn}
          </h1>

          {/* Progress Card */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">
                      {t('course.progress') || 'Progress'}
                    </span>
                    <span className="text-sm font-bold">
                      {Math.round(enrollment.progressPercent)}%
                    </span>
                  </div>
                  <Progress value={enrollment.progressPercent} className="h-2" />
                  <p className="text-sm text-muted-foreground mt-2">
                    {completedLessons} / {totalLessons} {t('course.lessonsCompleted') || 'lessons completed'}
                  </p>
                </div>
                {enrollment.progressPercent === 100 ? (
                  <div className="space-y-4">
                    <Button size="lg" variant="default" className="w-full gap-2" onClick={handleDownloadCertificate}>
                      <Award className="h-5 w-5" />
                      {t('course.downloadCertificate') || 'Download Certificate'}
                    </Button>
                    <ExamRequestForm 
                      courseId={courseId!} 
                      userEmail={user?.email}
                      userName={user?.name}
                    />
                  </div>
                ) : firstIncompleteLesson ? (
                  <Button 
                    size="lg" 
                    asChild
                    className="gap-2"
                  >
                    <Link to={`/dashboard/courses/${courseId}/lessons/${firstIncompleteLesson.id}`}>
                      <Play className="h-5 w-5" />
                      {firstIncompleteLesson.progress?.lastPosition 
                        ? t('course.continueLesson') || 'Continue Lesson'
                        : t('course.startLearning') || 'Start Learning'}
                    </Link>
                  </Button>
                ) : null}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Course Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Modules and Lessons */}
          <div className="lg:col-span-2 space-y-4">
            <h2 className="text-xl font-semibold mb-4">
              {t('course.curriculum') || 'Course Curriculum'}
            </h2>

            {modules.length === 0 ? (
              <Card>
                <CardContent className="pt-6 text-center text-muted-foreground">
                  {t('course.noContent') || 'No content available yet'}
                </CardContent>
              </Card>
            ) : (
              modules.map((module, moduleIndex) => {
                const isExpanded = expandedModules.has(module.id);
                const moduleCompleted = module.lessons.every(l => l.progress?.isCompleted);
                const completedCount = module.lessons.filter(l => l.progress?.isCompleted).length;
                const moduleProgress = module.lessons.length > 0
                  ? (completedCount / module.lessons.length) * 100
                  : 0;

                return (
                  <Card key={module.id}>
                    <CardHeader 
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => toggleModule(module.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm font-medium text-muted-foreground">
                              {t('course.module') || 'Module'} {moduleIndex}
                            </span>
                            {moduleCompleted && (
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            )}
                          </div>
                          <CardTitle className="text-lg">
                            {isRTL ? module.titleAr : module.titleEn}
                          </CardTitle>
                          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                            <span>{completedCount}/{module.lessons.length} {t('course.lessons') || 'lessons'}</span>
                            <span>
                              {(() => {
                                const totalDuration = module.lessons.reduce((sum, l) => sum + (l.videoDuration || 0), 0);
                                const hours = Math.floor(totalDuration / 3600);
                                const minutes = Math.floor((totalDuration % 3600) / 60);
                                return hours > 0 ? `${hours}h ${minutes}min` : `${minutes} min`;
                              })()}
                            </span>
                            <Progress value={moduleProgress} className="h-1 w-24" />
                          </div>
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                        )}
                      </div>
                    </CardHeader>

                    {isExpanded && (
                      <CardContent className="pt-0">
                        <div className="space-y-2">
                          {module.lessons.map((lesson, lessonIndex) => {
                            const isCompleted = lesson.progress?.isCompleted;
                            const hasProgress = lesson.progress && lesson.progress.lastPosition > 0;

                            return (
                              <Link
                                key={lesson.id}
                                to={`/dashboard/courses/${courseId}/lessons/${lesson.id}`}
                                className="block p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                              >
                                <div className="flex items-start gap-3">
                                  <div className="flex-shrink-0 mt-1">
                                    {isCompleted ? (
                                      <CheckCircle
                                        className="h-5 w-5 text-green-600 cursor-pointer hover:text-orange-500 transition-colors"
                                        onClick={(e: any) => { e.preventDefault(); e.stopPropagation(); handleMarkIncomplete(lesson.id); }}
                                        title="Click to mark incomplete"
                                      />
                                    ) : lesson.contentType === 'video' ? (
                                      <Play className="h-5 w-5 text-primary" />
                                    ) : lesson.contentType === 'quiz' ? (
                                      <FileText className="h-5 w-5 text-blue-600" />
                                    ) : (
                                      <FileText className="h-5 w-5 text-muted-foreground" />
                                    )}
                                  </div>

                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="text-xs text-muted-foreground">
                                        {lessonIndex + 1}
                                      </span>
                                      <h4 className="font-medium truncate">
                                        {isRTL ? lesson.titleAr : lesson.titleEn}
                                      {isCompleted && (
                                        <CheckCircle className="h-4 w-4 text-green-600 ml-2" />
                                      )}
                                      </h4>
                                    </div>

                                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                      {lesson.contentType === 'video' && lesson.videoDuration && (
                                        <span className="flex items-center gap-1">
                                          <Clock className="h-3 w-3" />
                                          {Math.floor(lesson.videoDuration / 60)} min
                                        </span>
                                      )}
                                      {hasProgress && !isCompleted && (
                                        <span className="text-primary font-medium">
                                          {t('course.inProgress') || 'In Progress'}
                                        </span>
                                      )}
                                      {isCompleted && (
                                        <span className="text-green-600 font-medium">
                                          {t('course.completed') || 'Completed'}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </Link>
                            );
                          })}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                );
              })
            )}
          </div>

          {/* Sidebar - Course Info */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle>{t('course.courseInfo') || 'Course Information'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium mb-2">
                    {t('course.instructor') || 'Instructor'}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {course.instructor_name}
                  </p>
                </div>

                <div>
                  <h4 className="text-sm font-medium mb-2">
                    {t('course.enrolled') || 'Enrolled'}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {new Date(enrollment.enrolledAt).toLocaleDateString()}
                  </p>
                </div>

                {course.duration_hours > 0 && (
                  <div>
                    <h4 className="text-sm font-medium mb-2">
                      {t('course.duration') || 'Duration'}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {course.duration_hours} {t('course.hours') || 'hours'}
                    </p>
                  </div>
                )}

                <div>
                  <h4 className="text-sm font-medium mb-2">
                    {t('course.level') || 'Level'}
                  </h4>
                  <p className="text-sm text-muted-foreground capitalize">
                    {i18n.language === 'ar' ? ({ beginner: 'مبتدئ', intermediate: 'متوسط', advanced: 'متقدم' } as Record<string,string>)[course.level] || course.level : course.level}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
